
import { Link } from "react-router-dom";
import { X, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import LanguageSelector, { SupportedLanguage } from "@/components/multilingual/LanguageSelector";
import { translations, useTranslation } from "@/lib/i18n";

interface CookieBannerAlertProps {
  language: SupportedLanguage;
  onLanguageChange: (value: SupportedLanguage) => void;
  onAcceptAll: () => void;
  onAcceptEssential: () => void;
  onOpenSettings: () => void;
  onDismiss: () => void;
}

export function CookieBannerAlert({
  language,
  onLanguageChange,
  onAcceptAll,
  onAcceptEssential,
  onOpenSettings,
  onDismiss
}: CookieBannerAlertProps) {
  // Using both approaches - classic for backward compatibility
  const t = translations.cookieBanner[language];
  
  // New approach (could replace the above in future updates)
  // const { t } = useTranslation(language);
  // const cookieBanner = t.cookieBanner;
  
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4">
      <Alert className="bg-background border shadow-lg max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <AlertDescription className="flex-1">
            <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
              <LanguageSelector 
                language={language} 
                onChange={onLanguageChange}
              />
            </div>
            <p className="mb-2">
              {t.description}{" "}
              <Link to="/cookie-policy" className="font-medium underline">
                {t.cookiePolicy}
              </Link>{" "}
              {t.and}{" "}
              <Link to="/privacy-policy" className="font-medium underline">
                {t.privacyPolicy}
              </Link>
              .
            </p>
          </AlertDescription>
          
          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            <Button variant="outline" size="sm" onClick={onOpenSettings}>
              <Settings className="h-4 w-4 mr-2" />
              {t.cookieSettings}
            </Button>
            <Button variant="outline" size="sm" onClick={onAcceptEssential}>
              {t.acceptEssential}
            </Button>
            <Button size="sm" onClick={onAcceptAll}>
              {t.acceptAll}
            </Button>
            <Button variant="ghost" size="sm" className="ml-auto md:ml-0" onClick={onDismiss}>
              <X className="h-4 w-4" />
              <span className="sr-only">{t.close}</span>
            </Button>
          </div>
        </div>
      </Alert>
    </div>
  );
}

export default CookieBannerAlert;
